import { customInstance } from "@repo/api-client";

/**
 * Client contract for the onboarding-wizard REST surface (`/api/v1/venues/{venueId}/...`, §C.1).
 * Goes through `@repo/api-client`'s fetch mutator (bearer token + typed `ApiError`), NOT a raw fetch
 * — a 409 (stale write / incomplete / go-live-blocked) and a 422 come back as `ApiError` carrying the
 * RFC 9457 problem. Matches the shell's `staffApi` / `scheduleApi` thin-accessor pattern (the Orval
 * client is regenerated from the backend OpenAPI at serial integration via `just gen`).
 */

/** The seven §C.1 steps, in wizard order: the four mandatory first, then the optional trio. */
export const STEP_ORDER = [
  "PROFILE",
  "ADDRESS",
  "HOURS",
  "CAPACITY",
  "PHOTOS",
  "STAFF",
  "LISTING_AUTH",
] as const;

export type OnboardingStep = (typeof STEP_ORDER)[number];
export type StepState = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETE";

/** Router state the setup banner sends so the wizard opens on the go-live surface. */
export interface WizardLocationState {
  activation?: boolean;
}

export interface OnboardingProgressResponse {
  venueId: string;
  lifecycle: string;
  steps: Record<OnboardingStep, StepState>;
  mandatorySteps: OnboardingStep[];
  mandatoryComplete: boolean;
  missingMandatory: OnboardingStep[];
  version: number;
  venueVersion: number;
}

export interface CapacityResponse {
  venueId: string;
  mode: string;
  totalCovers: number | null;
  version: number;
}

export interface ListingAuthorizationResponse {
  granted: boolean;
  decision: string | null;
  policyVersion: string | null;
  currentPolicyVersion: string;
  reConfirmRequired: boolean;
  decidedAt: string | null;
  version: number;
}

export interface VenueProfileResponse {
  venueId: string;
  displayName: string;
  legalName: string | null;
  category: string;
  contactPhone: string | null;
  contactEmail: string | null;
  version: number;
}

export interface ProfileRequest {
  displayName: string;
  legalName?: string | null;
  category: string;
  contactPhone?: string | null;
  contactCountry?: string | null;
  contactEmail?: string | null;
  expectedVersion: number;
}

/** Structured venue location (mirrors the backend `VenueLocationResponse`; all fields optional when unset). */
export interface VenueLocationResponse {
  streetLine1?: string;
  streetLine2?: string | null;
  locality?: string;
  postalCode?: string;
  region?: string | null;
  country?: string;
  formattedAddress?: string;
  lat?: number;
  lng?: number;
  version?: number;
}

/** The confirm-location payload (mirrors `ConfirmLocationRequest`); saving it marks the ADDRESS step complete. */
export interface ConfirmLocationRequestBody {
  streetLine1: string;
  streetLine2?: string | null;
  locality: string;
  postalCode: string;
  region?: string | null;
  country: string;
  formattedAddress: string;
  lat: number;
  lng: number;
  expectedVersion: number;
  confirmOutOfCountry?: boolean;
}

export type ListingDecision = "GRANTED" | "DECLINED" | "DEFERRED";

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}`;

export const onboardingApi = {
  getProgress: (venueId: string) =>
    customInstance<OnboardingProgressResponse>(`${base(venueId)}/onboarding`, { method: "GET" }),

  getProfile: (venueId: string) =>
    customInstance<VenueProfileResponse>(`${base(venueId)}/profile`, { method: "GET" }),

  updateProfile: (venueId: string, body: ProfileRequest) =>
    customInstance<VenueProfileResponse>(`${base(venueId)}/profile`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  getLocation: (venueId: string) =>
    customInstance<VenueLocationResponse | null>(`${base(venueId)}/location`, { method: "GET" }),

  /** Confirm the venue's structured address + pin. Marks the ADDRESS onboarding step COMPLETE server-side. */
  confirmLocation: (venueId: string, body: ConfirmLocationRequestBody) =>
    customInstance<VenueLocationResponse>(`${base(venueId)}/location`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  getCapacity: (venueId: string) =>
    customInstance<CapacityResponse | null>(`${base(venueId)}/capacity`, { method: "GET" }),

  putCapacity: (venueId: string, totalCovers: number, expectedVersion: number) =>
    customInstance<CapacityResponse>(`${base(venueId)}/capacity`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ totalCovers, expectedVersion }),
    }),

  getListingAuthorization: (venueId: string) =>
    customInstance<ListingAuthorizationResponse>(`${base(venueId)}/listing-authorization`, {
      method: "GET",
    }),

  recordListingDecision: (venueId: string, decision: ListingDecision, expectedVersion: number) =>
    customInstance<ListingAuthorizationResponse>(`${base(venueId)}/listing-authorization`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ decision, expectedVersion }),
    }),

  complete: (venueId: string) =>
    customInstance<{ lifecycle: string }>(`${base(venueId)}/onboarding/complete`, {
      method: "POST",
    }),

  /**
   * Attempt READY → LIVE (the four go-live gates). 200 → `{ lifecycle: "LIVE" }`. A blocked attempt
   * throws an `ApiError` 409 whose problem carries `errorCode: "GO_LIVE_BLOCKED"` + `unmetGates: string[]`
   * so the caller can guide the manager to the missing gate (DPA / trial).
   */
  goLive: (venueId: string) =>
    customInstance<{ lifecycle: string }>(`${base(venueId)}/go-live`, { method: "POST" }),

  /** Accept the current DPA (root/manager click-wrap) — satisfies the DPA go-live gate. */
  acceptDpa: (venueId: string) =>
    customInstance<{ acceptanceId: string }>(`${base(venueId)}/dpa/acceptance`, { method: "POST" }),
};

/** The `["venue", venueId, "onboarding"]` query-key family this feature owns (eventQueryKeyMap). */
export const onboardingKeys = {
  progress: (venueId: string) => ["venue", venueId, "onboarding"] as const,
  profile: (venueId: string) => ["venue", venueId, "onboarding", "profile"] as const,
  location: (venueId: string) => ["venue", venueId, "onboarding", "location"] as const,
  capacity: (venueId: string) => ["venue", venueId, "onboarding", "capacity"] as const,
  listingAuth: (venueId: string) => ["venue", venueId, "onboarding", "listing-auth"] as const,
};

/**
 * Resume-at-first-incomplete (R6): the first step in wizard order whose state is not COMPLETE. When
 * every step is complete the wizard lands on the last step (the review/completion surface).
 */
export function firstIncompleteStep(steps: Record<OnboardingStep, StepState>): OnboardingStep {
  // Fallback = the last step (the review/completion surface) when every step is complete.
  return STEP_ORDER.find((s) => steps[s] !== "COMPLETE") ?? "LISTING_AUTH";
}

/**
 * Whether the mandatory steps are all COMPLETE (mirrors the server's `mandatoryComplete`). HOURS is no
 * longer mandatory — a venue can finish onboarding and set its schedule later.
 */
export function isMandatoryComplete(steps: Record<OnboardingStep, StepState>): boolean {
  return (["PROFILE", "ADDRESS", "CAPACITY"] as OnboardingStep[]).every(
    (s) => steps[s] === "COMPLETE",
  );
}
